import { useEffect } from 'react';
import { Button, InlineLoading } from '@carbon/react';
import { Copy, ErrorFilled } from '@carbon/react/icons';
import {
  EventsPanel,
  EventsPanelOpenButton,
} from 'components/events-panel/events-panel';
import { useAtom, useAtomValue } from 'jotai';
import { useHistory } from 'react-router-dom';
import routes from '~/routes';
import { ScenarioFormLayout } from 'components/scenario-form-layout';
import type { ScenarioNavItem } from 'components/scenario-nav/types';
import type { ScenarioMapFeature } from 'components/scenario-map/types';
import type { AreaSelection, DiscountRateScenarioFormData } from './types';
import { ScenarioFormProvider, useScenarioForm } from './contexts';
import {
  areaGeometriesAtom,
  categoriesLoadableAtom,
  unitTypesLoadableAtom,
  enumsLoadableAtom,
  cloneScenarioLoadableAtom,
  toastAtom,
  drsMapConfigAtom,
} from './atoms';
import { ScenarioGlobalConfigs } from 'components/scenario-global-configs';
import { ScenarioAreaConfigs } from 'components/scenario-area-configs';
import { DiscountRateTable } from './discount-rate-table';
import { RiskingTable } from './risking-table';
import {
  GLOBAL_SELECTION_ID,
  getAreaDisplayName,
  getAreaBadge,
  type FormMode,
} from './utils';
import { getMemoizedOverlappingAreaIds } from 'utils/geometry-overlap';
import styles from './scenario-form.module.scss';

interface ScenarioFormProps {
  initialValues: DiscountRateScenarioFormData;
  mode: FormMode;
  scenarioId?: string;
  commentsUrl?: string;
  eventsUrl?: string;
}

/**
 * Main form component for creating/editing discount rate scenarios.
 * Wraps content in ScenarioFormProvider and renders the full layout.
 * @param {ScenarioFormProps} props - The form props
 * @return {JSX.Element} The scenario form component
 */
export function ScenarioForm({
  initialValues,
  mode,
  scenarioId,
  commentsUrl,
  eventsUrl,
}: ScenarioFormProps): JSX.Element {
  const geometries = useAtomValue(areaGeometriesAtom);

  return (
    <ScenarioFormProvider
      initialValues={initialValues}
      mode={mode}
      scenarioId={scenarioId}
      geometries={geometries}
    >
      <ScenarioFormContent
        scenarioId={scenarioId}
        commentsUrl={commentsUrl}
        eventsUrl={eventsUrl}
      />
    </ScenarioFormProvider>
  );
}

/**
 * Inner content component that consumes the form context.
 * Assembles ScenarioFormLayout with DRS-specific content panels.
 * @return {JSX.Element} The form content layout
 */
function ScenarioFormContent({
  scenarioId,
  commentsUrl,
  eventsUrl,
}: {
  scenarioId?: string;
  commentsUrl?: string;
  eventsUrl?: string;
}): JSX.Element {
  const {
    form,
    mode,
    selectedId,
    setSelectedId,
    addArea,
    removeArea,
    handleSave,
    isSaving,
  } = useScenarioForm();

  const geometries = useAtomValue(areaGeometriesAtom);
  const [toast, setToast] = useAtom(toastAtom);
  const [cloneState, triggerClone] = useAtom(cloneScenarioLoadableAtom);
  const history = useHistory();

  const categoriesState = useAtomValue(categoriesLoadableAtom);
  const unitTypesState = useAtomValue(unitTypesLoadableAtom);
  const enumsState = useAtomValue(enumsLoadableAtom);
  const isCloning = cloneState.state === 'loading';

  useEffect(() => {
    if (categoriesState.state === 'hasError') {
      setToast({ message: 'Failed to load area categories', type: 'error' });
    }
  }, [categoriesState.state, setToast]);

  useEffect(() => {
    if (unitTypesState.state === 'hasError') {
      setToast({ message: 'Failed to load unit types', type: 'error' });
    }
  }, [unitTypesState.state, setToast]);

  useEffect(() => {
    if (enumsState.state === 'hasError') {
      setToast({ message: 'Failed to load enum values', type: 'error' });
    }
  }, [enumsState.state, setToast]);

  useEffect(() => {
    if (Object.keys(geometries).length > 0) {
      form.validate('change');
    }
  }, [geometries, form]);

  const handleClone = () => {
    if (!scenarioId) return;
    triggerClone(Number(scenarioId))
      .then((data) => {
        history.push(data.href);
      })
      .catch(() => {});
  };

  const handleCancel = () => {
    history.push(routes.discountRateScenariosPath);
  };

  const scenarioName = form.getFieldValue('global.name');

  return (
    <form.Subscribe
      selector={(state) => {
        const { areas } = state.values;
        const overlapping = getMemoizedOverlappingAreaIds(areas, geometries);

        const navItems: ScenarioNavItem[] = areas.map(
          (area: AreaSelection) => ({
            id: area.id,
            name: getAreaDisplayName(area),
            badge: getAreaBadge(area),
            hasOverlap: overlapping.has(area.id),
            isNew: area.status === 'new',
          })
        );

        const mapFeatures: ScenarioMapFeature[] = areas
          .filter((a: AreaSelection) => geometries[a.id])
          .map((a: AreaSelection) => ({
            id: a.id,
            geometry: geometries[a.id],
            type:
              a.geometryType === 'slot'
                ? ('well-stick' as const)
                : ('polygon' as const),
          }));

        const currentArea =
          selectedId !== GLOBAL_SELECTION_ID
            ? areas.find((a: AreaSelection) => a.id === selectedId)
            : undefined;

        return {
          navItems,
          mapFeatures,
          contentTitle: currentArea
            ? getAreaDisplayName(currentArea)
            : 'Scenario Details',
          canSubmit: state.canSubmit,
          // The form validator's reason, shown beside Save so a blocked save names its cause
          // (e.g. the combination two discount rate lines both cover).
          saveBlockedReason:
            typeof state.errorMap.onChange === 'string'
              ? state.errorMap.onChange
              : undefined,
          isDirty: state.isDirty,
          hasOverlap: overlapping.size > 0,
          selectedAreaOverlap:
            currentArea && overlapping.has(currentArea.id)
              ? 'Overlaps with an existing area'
              : undefined,
        };
      }}
    >
      {({
        navItems,
        mapFeatures,
        contentTitle,
        canSubmit,
        saveBlockedReason,
        isDirty,
        hasOverlap,
        selectedAreaOverlap,
      }) => {
        const canSave =
          (mode === 'create' ? canSubmit : canSubmit && isDirty) && !hasOverlap;

        return (
          <ScenarioFormLayout
            title={
              mode === 'create'
                ? 'New Discount Rate Scenario'
                : scenarioName || 'Edit Discount Rate Scenario'
            }
            mapFeatures={mapFeatures}
            mapFocusedId={typeof selectedId === 'number' ? selectedId : null}
            mapConfigAtom={drsMapConfigAtom}
            navItems={navItems}
            navSelectedId={selectedId}
            onNavSelect={setSelectedId}
            onNavAdd={addArea}
            onNavDelete={removeArea}
            navGlobalLabel="Scenario Details"
            contentTitle={contentTitle}
            overlapError={selectedAreaOverlap}
            headerActions={
              <div className={styles.headerActions}>
                {mode === 'edit' && (
                  <>
                    <EventsPanelOpenButton />
                    <Button
                      kind="tertiary"
                      size="md"
                      renderIcon={Copy}
                      disabled={isCloning}
                      onClick={handleClone}
                    >
                      {isCloning ? <InlineLoading /> : 'Clone Scenario'}
                    </Button>
                  </>
                )}
                {/* Here rather than ContentHeader's saveBlockedReason: this form keeps Save in
                    headerActions, and ContentHeader shows the reason only beside its own Save.
                    A polite region, always mounted, so each change is read once. */}
                <span
                  id="save-blocked-reason"
                  className={styles.saveBlocked}
                  role="status"
                >
                  {saveBlockedReason && (
                    <>
                      <ErrorFilled className={styles.saveBlockedIcon} />
                      {saveBlockedReason}
                    </>
                  )}
                </span>
                <Button kind="secondary" size="md" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  kind="primary"
                  size="md"
                  disabled={!canSave || isSaving}
                  aria-describedby={
                    saveBlockedReason ? 'save-blocked-reason' : undefined
                  }
                  onClick={handleSave}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            }
            toast={toast}
            onToastClose={() => setToast(null)}
            eventsPanel={
              mode === 'edit' && commentsUrl && eventsUrl ? (
                <EventsPanel
                  header={scenarioName || ''}
                  title="Discount Rate Scenario"
                  helperText="Share information about this scenario."
                  commentsHref={commentsUrl}
                  eventsHref={eventsUrl}
                />
              ) : undefined
            }
          >
            {selectedId === GLOBAL_SELECTION_ID ? (
              <ScenarioGlobalConfigs form={form} />
            ) : (
              <form.Subscribe
                selector={(state) =>
                  state.values.areas.findIndex(
                    (a: AreaSelection) => a.id === selectedId
                  )
                }
              >
                {(areaIndex) => {
                  if (areaIndex < 0) return null;
                  return (
                    <div className={styles.areaSections}>
                      <ScenarioAreaConfigs
                        form={form}
                        areaIndex={areaIndex}
                        enabledTypes={['area', 'unit', 'slot']}
                        notesPlaceholder="Notes about this area's discount rate overrides..."
                      />
                      <div className={styles.sectionDivider}>
                        <DiscountRateTable areaIndex={areaIndex} />
                      </div>
                      <div className={styles.sectionDivider}>
                        <RiskingTable areaIndex={areaIndex} />
                      </div>
                    </div>
                  );
                }}
              </form.Subscribe>
            )}
          </ScenarioFormLayout>
        );
      }}
    </form.Subscribe>
  );
}
